import * as React from "react";
import { useQuery } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { LoadingDots } from "web-ui";

import { getMajorMinorVersions } from "api/dashboardBackend";
import DuoErrorScreen, { DuoErrorType } from "components/DuoErrorScreen";
import TopbarWithCloseButton, {
  type Tab,
} from "components/common/TopbarWithCloseButton";
import VersionSelect from "components/common/VersionSelect";
import ReleaseHistoryContent from "components/history/ReleaseHistoryContent";
import styles from "components/history/ReleaseHistoryPage.module.scss";
import useErrorScreen from "components/useErrorScreen";
import { ALL_PLATFORMS, PlatformId } from "util/platform";

const ReleaseHistoryPage = () => {
  const { platform } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fromVersion, setFromVersion] = React.useState(
    searchParams.get("fromVersion") ?? undefined,
  );
  const [toVersion, setToVersion] = React.useState(
    searchParams.get("toVersion") ?? undefined,
  );
  const { isError, error, isLoading, data } = useQuery({
    queryFn: () => getMajorMinorVersions(platform as PlatformId),
    queryKey: [platform],
  });

  const errorComponent = useErrorScreen(isError, error);

  if (errorComponent) {
    return errorComponent;
  }

  // eslint-disable-next-line
  const includedPlatforms = (platform: string): platform is PlatformId =>
    ALL_PLATFORMS.includes(platform);

  if (platform === undefined || !includedPlatforms(platform)) {
    return (
      <DuoErrorScreen
        error={Error(`Invalid platform: ${platform}.`)}
        errorType={DuoErrorType.UnknownError}
      />
    );
  }

  const fromVersionChangeCallback = (value: string) => {
    setFromVersion(value);
    const newParams = new URLSearchParams(searchParams);
    if (toVersion !== undefined) {
      newParams.set("toVersion", toVersion);
    }
    newParams.set("fromVersion", value);
    setSearchParams(newParams);
  };

  const toVersionChangeCallback = (value: string) => {
    setToVersion(value);
    const newParams = new URLSearchParams(searchParams);
    if (fromVersion !== undefined) {
      newParams.set("fromVersion", fromVersion);
    }
    newParams.set("toVersion", value);
    setSearchParams(newParams);
  };

  if (fromVersion === undefined || toVersion === undefined || isLoading) {
    if (data !== undefined && data.length > 0) {
      if (fromVersion === undefined) {
        const newFromVersion = `${data[data.length - 1].major}.${
          data[data.length - 1].minor
        }`;
        fromVersionChangeCallback(newFromVersion);
      }
      if (toVersion === undefined) {
        const newToVersion = `${data[0].major}.${data[0].minor}`;
        toVersionChangeCallback(newToVersion);
      }
    }
    return <LoadingDots type="screen-white" />;
  }

  const tabs: Tab[] = [
    {
      href: `/history/${platform}`,
      isActive: true,
      name: "History Releases",
    },
  ];

  if (platform === PlatformId.Android) {
    tabs.push({
      href: `/crashes/${platform}`,
      name: "Crashes",
    });
  }

  return (
    <>
      <TopbarWithCloseButton tabs={tabs} />
      <div className={styles.queryContainer}>
        <VersionSelect
          onChangeCallback={fromVersionChangeCallback}
          platformId={platform}
          queryPrompt="From Version:"
          value={fromVersion ?? "Loading..."}
        />
        <VersionSelect
          onChangeCallback={toVersionChangeCallback}
          platformId={platform}
          queryPrompt="To Version:"
          value={toVersion ?? "Loading..."}
        />
      </div>

      <div>
        <ReleaseHistoryContent
          fromVersion={fromVersion}
          platformId={platform}
          toVersion={toVersion}
        />
      </div>
    </>
  );
};

export default ReleaseHistoryPage;
